import React, { useState, useEffect } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogTitle from "@material-ui/core/DialogTitle";
import HistoricalSection from "./HistoricalSection"; //Componente para renderizar as seções do histórico

import { useUser } from "../../context/userContext";
import { useDot } from "../../context/automatonContext";
import { makeStyles } from "@material-ui/core/styles";
import styles from "../../assets/jss/material-dashboard-react/components/dialogHistorical";
import { Button } from "@material-ui/core";
import constants from "../../constants/tutorialAutomaton";
import "./classes.css";

const useStyles = makeStyles(styles);

/**
 * Componente que exibe o diálogo histórico de autômatos criados pelo usuário
 *
 * @param {Object} props Propriedades passadas para o componente.
 * @returns {JSX.Element} Componente JSX.
 */
export default function HistoricalDialog(props) {
  const { language, user } = useUser();
  const {
    setDot,
    setDotName,
    setCreatedDate,
    isActiveDialogHistorical,
    setActiveDialogHistorical,
    setArrayElements,
    setMachineStates,
    setMachineTransitions,
  } = useDot();

  const [checked, setChecked] = useState(true);
  const [historicalToday, setHistoricalToday] = useState([]);
  const [historicalYesterday, setHistoricalYesterday] = useState([]);
  const [historicalLastWeeks, setHistoricalLastWeeks] = useState([]);
  const [historicalMachineSorted, setHistoricalMachineSorted] = useState([]);

  const classes = useStyles();

  /**
   * Ordena o histórico com base no parâmetro.
   *
   * @param {Array} historical Histórico de máquinas.
   * @param {boolean} isAscending Define o padrão da ordenação. Se true é Ascendente e false Descendente
   * @returns {Array} Histórico ordenado.
   */
  const sortHistorical = (historical, isAscending) => {
    return historical?.slice().sort((a, b) => {
      const dateA = new Date(a.lastUpdatedDate); // Converte o LocalDateTime para Date
      const dateB = new Date(b.lastUpdatedDate); // Converte o LocalDateTime para Date

      return isAscending ? dateA - dateB : dateB - dateA;
    });
  };

  /**
   * Atualiza os históricos separados por períodos, padrão de 3 períodos. (HOJE, ONTEM, ÚLTIMAS SEMANAS)
   *
   * @param {Array} sortedHistorical Histórico ordenado.
   */
  const updateHistoricalPeriods = (sortedHistorical) => {
    const today = new Date();
    const tempToday = [];
    const tempYesterday = [];
    const tempLastWeeks = [];

    sortedHistorical.forEach((element) => {
      // Formatação de variables
      let mcToHtml = toDOTSimple(element.variables);
      let testMcLength = mcToHtml.split("\n");
      if (testMcLength.length > 2) {
        mcToHtml = testMcLength[0] + "\n" + testMcLength[1] + "\n ...";
      }

      const date = new Date(element.lastUpdatedDate);
      const formattedDate = `${date.getDate()}/${
        date.getMonth() + 1
      }/${date.getFullYear()} - ${date.getHours()}:${String(
        date.getMinutes()
      ).padStart(2, "0")}`;

      const obj = {
        name: element.name,
        value: mcToHtml,
        date: formattedDate,
      };

      if (isToday(element, today)) {
        tempToday.push(obj);
      } else if (isYesterday(element, today)) {
        tempYesterday.push(obj);
      } else {
        tempLastWeeks.push(obj);
      }
    });

    setHistoricalToday(tempToday);
    setHistoricalYesterday(tempYesterday);
    setHistoricalLastWeeks(tempLastWeeks);
  };

  /**
   * Verifica se a data é hoje.
   *
   * @param {Object} element Elemento do histórico.
   * @param {Date} today Data atual.
   * @returns {boolean} Verdadeiro se a data for hoje.
   */
  const isToday = (element, today) =>
    today.getDate() === new Date(element.lastUpdatedDate).getDate() &&
    today.getMonth() === new Date(element.lastUpdatedDate).getMonth() &&
    today.getFullYear() === new Date(element.lastUpdatedDate).getFullYear();

  /**
   * Verifica se a data é ontem.
   *
   * @param {Object} element Elemento do histórico.
   * @param {Date} today Data atual.
   * @returns {boolean} Verdadeiro se a data for ontem.
   */
  const isYesterday = (element, today) =>
    today.getDate() - 1 === new Date(element.lastUpdatedDate).getDate() &&
    today.getMonth() === new Date(element.lastUpdatedDate).getMonth() &&
    today.getFullYear() === new Date(element.lastUpdatedDate).getFullYear();

  /**
   * Simplifica a representação do autômato em DOT.
   *
   * @param {string} dot Representação do autômato em DOT.
   * @returns {string} Representação simplificada.
   */
  const toDOTSimple = (Dot) => {
    try {
      let dot = "";
      const completeRules = Dot.split("\n\n");
      const completeTransitions = completeRules[1];

      if (completeTransitions !== undefined) {
        const arrayTransitions = completeTransitions.split("\n");

        for (let i = 0; i < arrayTransitions.length; i++) {
          dot +=
            arrayTransitions[i].split("label")[0] +
            arrayTransitions[i].split('"')[1] +
            "]\n";
        }
      }

      let importantStates = Dot.split("\n\n")[1];

      importantStates = importantStates.split("\n}")[0];

      const arrayImportantStates = importantStates.split("\n");

      for (let i = 0; i < arrayImportantStates.length; i++) {
        dot += arrayImportantStates[i].split("[")[0] + "=";
        if (arrayImportantStates[i].split("=")[1].split("]")[0] === "diamond") {
          dot += "initial\n";
        } else if (
          arrayImportantStates[i].split("=")[1].split("]")[0] === "square"
        ) {
          dot += "final\n";
        } else if (
          arrayImportantStates[i].split("=")[1].split("]")[0] === "Mdiamond"
        ) {
          dot += "initial/final\n";
        }
      }

      return dot;
    } catch (e) {
      alert(e);
    }
  };

  useEffect(() => {
    if (user.historicalMachine) {
      const sortedHistorical = sortHistorical(user.historicalMachine, false);
      setHistoricalMachineSorted(sortedHistorical);
      updateHistoricalPeriods(sortedHistorical);
    }
  }, [user.historicalMachine]);

  /**
   * Fecha o diálogo.
   */
  const handleClose = () => {
    setActiveDialogHistorical(false);
  };

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  /**
   * Renderiza o autômato do histórico.
   *
   * @param {number} index Índice do histórico.
   * @param {string} time Período do histórico (today, yesterday, lastWeeks).
   */
  const drawHistoricalAutomaton = async (index, time) => {
    if (time === "yesterday") {
      index += historicalToday.length;
    } else if (time === "lastWeeks") {
      index += historicalToday.length + historicalYesterday.length;
    }

    const dot = historicalMachineSorted[index];
    await clearMachine();
    setDot(dot.variables);
    setDotName(dot.name);
    setCreatedDate(dot.createdDate);
    setActiveDialogHistorical(false);
  };

  /**
   * Limpa os estados relacionados ao autômato para preparar o ambiente
   * para o desenho de um novo autômato. Essa função redefine variáveis
   * globais e propriedades relacionadas ao desenho do autômato.
   *
   * @async
   * @function clearMachine
   * @returns {Promise<void>} Uma promessa que é resolvida após todos os
   * estados terem sido redefinidos.
   */
  async function clearMachine() {
    return new Promise((resolve) => {
      props.setStars([]);
      setDot("");
      setMachineStates([]);
      setMachineTransitions([]);
      setArrayElements([]);
      props.setChilds([]);
      resolve(); // Garante que a função é resolvida antes de continuar
    });
  }

  return (
    <div>
      <Dialog
        checked={checked}
        onChange={handleChange}
        open={isActiveDialogHistorical}
        keepMounted
        onClose={handleClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle className={classes.dialog} id="alert-dialog-slide-title">
          <div className="wrapper-title">
            {constants.historic[language]}
            <div className="wrapper-button-close button-margin">
              <Button
                style={{ backgroundColor: "#999", color: "#FFF" }}
                onClick={handleClose}
              >
                X
              </Button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialog}>
          {historicalToday.length > 0 && (
            <HistoricalSection
              title={constants.today[language]}
              data={historicalToday}
              onButtonClick={(index) => drawHistoricalAutomaton(index, "today")}
            />
          )}
          {historicalYesterday.length > 0 && (
            <HistoricalSection
              title={constants.yesterday[language]}
              data={historicalYesterday}
              onButtonClick={(index) =>
                drawHistoricalAutomaton(index, "yesterday")
              }
            />
          )}
          {historicalLastWeeks.length > 0 && (
            <HistoricalSection
              title={constants.lastWeeks[language]}
              data={historicalLastWeeks}
              onButtonClick={(index) =>
                drawHistoricalAutomaton(index, "lastWeeks")
              }
            />
          )}
        </DialogContent>
        <DialogActions className={classes.dialog}></DialogActions>
      </Dialog>
    </div>
  );
}
